
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import { FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { NotificationsComponent } from 'app/layout/common/notifications/notifications.component';
import { QuickChatComponent } from 'app/layout/common/quick-chat/quick-chat.component';
import { SearchComponent } from 'app/layout/common/search/search.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { IncidenciaBadgeComponent } from 'app/layout/common/incidencia-badge/incidencia-badge.component';
import { RecetaBadgeComponent } from 'app/layout/common/receta-badge/receta-badge.component';
import { environment } from 'environments/environment';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { cloneDeep } from 'lodash-es';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'classy-layout',
    templateUrl: './classy.component.html',
    encapsulation: ViewEncapsulation.None,
    imports: [
    FuseLoadingBarComponent,
    FuseVerticalNavigationComponent,
    NotificationsComponent,
    UserComponent,
    MatIconModule,
    MatButtonModule,
    FuseFullscreenComponent,
    SearchComponent,
    IncidenciaBadgeComponent,
    RecetaBadgeComponent,
    RouterOutlet,
    QuickChatComponent,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule
]
})
export class ClassyLayoutComponent implements OnInit, OnDestroy {
    isScreenSmall: boolean;
    navigation: Navigation;
    originalNavigation: Navigation;
    user: User;
    searchControl: UntypedFormControl = new UntypedFormControl();
    public readonly version = environment.version;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _userService: UserService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                // Clone navigation to keep original reference
                if (!this.originalNavigation) {
                    this.originalNavigation = cloneDeep(navigation);
                }
                this.navigation = navigation;
            });

        // Subscribe to search control
        this.searchControl.valueChanges
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((value) => {
                const term = value?.toLowerCase() || '';
                this.filterNavigation(term);
            });

        // Subscribe to the user service
        this._userService.user$
            .pipe((takeUntil(this._unsubscribeAll)))
            .subscribe((user: User) => {
                this.user = user;
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    filterNavigation(term: string): void {
        // Guard against missing default navigation
        if (!this.originalNavigation || !this.originalNavigation.default) {
            return;
        }

        const defaultNav = cloneDeep(this.originalNavigation.default);

        if (!term) {
            this.navigation = {
                ...this.originalNavigation,
                default: defaultNav
            };
            return;
        }

        const filterItems = (items: any[]): any[] => {
            return items.reduce((acc, item) => {
                const matches = item.title && item.title.toLowerCase().includes(term);
                let children = [];
                if (item.children) {
                    children = filterItems(item.children);
                }

                if (matches || children.length > 0) {
                    acc.push({
                        ...item,
                        children: children.length > 0 ? children : item.children // Keep children if match is parent, or filtered children
                    });
                }
                return acc;
            }, []);
        };

        const filteredDefault = filterItems(defaultNav);

        this.navigation = {
            ...this.originalNavigation,
            default: filteredDefault
        };
    }
}
